import { formatLocalDate, toLocalDateKey } from '@/utils/localDateUtils';
import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import api from '@/services/api';
import MainLayout from '@/components/layout/MainLayout';
import { ReportPrintButton } from '@/components/reports/ReportPrintButton';
import { useDialog } from '@/hooks/useDialog';

interface LegalListItem {
  contrato_id: number;
  numero_contrato: string;
  nombre: string;
  apellido: string;
  tipo_documento: string;
  documento: string;
  telefono?: string;
  email?: string;
  propiedad: string;
  deuda_total: number;
  moneda: string;
  max_dias_atraso: number;
  pagos_pendientes: number;
  fecha_ingreso_lista_juridica?: string;
  notas_lista_juridica?: string;
}

interface Criteria {
  monto_minimo: number;
  meses_atraso: number;
  pagos_pendientes: number;
}

interface LegalCandidateRow extends LegalListItem {
  pago_id: number;
  inmueble_id: number;
  garantia: string;
  fecha_limite: string;
  monto: number;
}

export default function LegalListReport() {
  const { showAlert, showConfirm } = useDialog();
  const reportRef = useRef<HTMLDivElement>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [currentList, setCurrentList] = useState<LegalListItem[]>([]);
  const [candidates, setCandidates] = useState<LegalListItem[]>([]);
  const [candidateRows, setCandidateRows] = useState<LegalCandidateRow[]>([]);
  const [criteria, setCriteria] = useState<Criteria>({ monto_minimo: 50000, meses_atraso: 3, pagos_pendientes: 3 });
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState<'excel' | null>(null);
  const [showCriteriaModal, setShowCriteriaModal] = useState(false);
  const [editingCriteria, setEditingCriteria] = useState<Criteria | null>(null);
  const requestedTab = searchParams.get('tab') === 'candidates' ? 'candidates' : 'current';
  const [activeTab, setActiveTab] = useState<'current' | 'candidates'>(requestedTab);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    setActiveTab(requestedTab);
  }, [requestedTab]);

  const selectTab = (tab: 'current' | 'candidates') => {
    setActiveTab(tab);
    if (tab === 'candidates') {
      setSearchParams({ tab: 'candidates' });
    } else {
      setSearchParams({});
    }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await api.get('/reports/legal-list');
      const data = res.data?.data || res.data;
      setCurrentList(data.current_list || []);
      setCandidates(data.candidates || []);
      setCandidateRows(data.candidate_rows || []);
      setCriteria(data.criteria || { monto_minimo: 50000, meses_atraso: 3, pagos_pendientes: 3 });
    } catch (error) {
      console.error('Error loading report:', error);
    } finally {
      setLoading(false);
    }
  };

  const exportExcel = async () => {
    setExporting('excel');
    try {
      const res = await api.get('/reports/legal-list?export=excel');
      const filePath = res.data?.data?.file || res.data?.file;

      if (filePath) {
        // Download the file using the file path
        const baseURL = (api.defaults as any).baseURL || 'http://localhost/backend';
        const fileUrl = `${baseURL}/${filePath}`;

        // Fetch as blob and trigger download
        const response = await axios.get(fileUrl, {
          responseType: 'blob',
          withCredentials: true
        });

        const url = window.URL.createObjectURL(response.data);
        const a = document.createElement('a');
        a.href = url;
        a.download = `lista-juridica-${toLocalDateKey(new Date())}.csv`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }
    } catch (error) {
      console.error('Error exporting Excel:', error);
      showAlert({
        type: 'error',
        title: 'Error',
        message: 'Error al exportar Excel. Por favor, intente nuevamente.',
      });
    } finally {
      setExporting(null);
    }
  };

  const addToLegalList = async (contratoId: number) => {
    try {
      await api.post('/reports/legal-list/add', { contrato_id: contratoId });
      loadData();
    } catch (error) {
      console.error('Error adding to legal list:', error);
    }
  };

  const removeFromLegalList = async (contratoId: number) => {
    const confirmed = await showConfirm({
      title: 'Remover de lista',
      message: '¿Está seguro de remover este contrato de la lista jurídica?',
      type: 'warning',
    });
    if (!confirmed) return;
    
    try {
      await api.delete(`/reports/legal-list/remove/${contratoId}`);
      loadData();
    } catch (error) {
      console.error('Error removing from legal list:', error);
    }
  };

  const saveCriteria = async () => {
    if (!editingCriteria) return;
    try {
      await api.put('/reports/legal-list/criteria', { criteria: editingCriteria });
      setCriteria(editingCriteria);
      setShowCriteriaModal(false);
      loadData();
    } catch (error) {
      console.error('Error saving criteria:', error);
    }
  };

  const renderList = (items: LegalListItem[], isCurrentList: boolean) => {
    if (items.length === 0) {
      return (
        <div className="text-center py-12">
          <div className="text-6xl mb-4">{isCurrentList ? '✅' : '🔍'}</div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {isCurrentList ? 'No hay contratos en lista jurídica' : 'No hay candidatos'}
          </h3>
          <p className="text-gray-600">
            {isCurrentList
              ? 'No se encontraron contratos agregados a la lista jurídica'
              : 'No se encontraron candidatos que cumplan los criterios'
            }
          </p>
        </div>
      );
    }

    return (
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contrato</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cliente</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Propiedad</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Deuda</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Días Atraso</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Pagos Pendientes</th>
              {isCurrentList && (
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fecha Ingreso</th>
              )}
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Acciones</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {items.map((item) => (
              <tr key={item.contrato_id} className="hover:bg-gray-50 transition-colors">
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{item.numero_contrato}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  <div>
                    <div className="font-medium">{item.apellido}, {item.nombre}</div>
                    <div className="text-gray-500">{item.tipo_documento}: {item.documento}</div>
                  </div>
                </td>
                <td className="px-6 py-4 text-sm text-gray-900">{item.propiedad}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  <span className="font-semibold text-red-600">
                    {item.moneda} {Number(item.deuda_total).toLocaleString()}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                    {item.max_dias_atraso} días
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{item.pagos_pendientes}</td>
                {isCurrentList && (
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {item.fecha_ingreso_lista_juridica
                      ? formatLocalDate(item.fecha_ingreso_lista_juridica)
                      : '-'
                    }
                  </td>
                )}
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                  {isCurrentList ? (
                    <button
                      onClick={() => removeFromLegalList(item.contrato_id)}
                      className="text-red-600 hover:text-red-900 font-medium"
                    >
                      Remover
                    </button>
                  ) : (
                    <button
                      onClick={() => addToLegalList(item.contrato_id)}
                      className="text-blue-600 hover:text-blue-900 font-medium"
                    >
                      Agregar
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <MainLayout compact>
      <div className="w-full space-y-5">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-2 text-sm text-gray-600 mb-3">
            <Link to="/reports" className="hover:text-gray-900">Reportes</Link>
            <span>/</span>
            <span className="text-gray-900">Lista Jurídica</span>
          </div>
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 mb-1 flex items-center gap-2">
                <span className="text-3xl">⚖️</span>
                Lista Jurídica
              </h1>
              <p className="text-gray-600">Gestión de casos con morosidad significativa para acción legal</p>
            </div>
            <div className="flex flex-wrap gap-2">
              <ReportPrintButton
                title={activeTab === 'candidates' ? 'Inquilinos que deben pasar a jurídica' : 'Lista Jurídica Actual'}
                containerRef={reportRef}
                disabled={loading || exporting !== null || (activeTab === 'candidates' ? candidateRows.length === 0 : currentList.length === 0)}
              />
              <button
                onClick={() => {
                  setEditingCriteria(criteria);
                  setShowCriteriaModal(true);
                }}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                Configurar Criterios
              </button>
              <ReportPrintButton
                variant="pdf" label="PDF"
                title={activeTab === 'candidates' ? 'Inquilinos que deben pasar a jurídica' : 'Lista Jurídica Actual'}
                containerRef={reportRef}
                disabled={loading || exporting !== null || (activeTab === 'candidates' ? candidateRows.length === 0 : currentList.length === 0)}
              />
              <button
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                onClick={exportExcel}
                disabled={exporting !== null}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                {exporting === 'excel' ? 'Exportando...' : 'Excel'}
              </button>
            </div>
          </div>
        </div>

        {/* Criteria Info */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <h3 className="text-sm font-semibold text-blue-900 mb-2">Criterios Actuales</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm text-blue-800">
            <div>
              <span className="font-medium">Deuda Mínima:</span> UYU {Number(criteria.monto_minimo).toLocaleString()}
            </div>
            <div>
              <span className="font-medium">Meses de Atraso:</span> {criteria.meses_atraso} meses
            </div>
            <div>
              <span className="font-medium">Pagos Pendientes:</span> {criteria.pagos_pendientes} pagos
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div ref={reportRef} className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="border-b border-gray-200">
            <div className="flex">
              <button
                onClick={() => selectTab('current')}
                className={`px-6 py-3 font-medium text-sm border-b-2 transition-colors ${
                  activeTab === 'current'
                    ? 'border-orange-600 text-orange-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Lista Jurídica Actual ({currentList.length})
              </button>
              <button
                onClick={() => selectTab('candidates')}
                className={`px-6 py-3 font-medium text-sm border-b-2 transition-colors ${
                  activeTab === 'candidates'
                    ? 'border-orange-600 text-orange-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Candidatos ({candidates.length})
              </button>
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mb-2"></div>
                <p className="text-gray-600">Cargando reporte...</p>
              </div>
            </div>
          ) : activeTab === 'current' ? (
            renderList(currentList, true)
          ) : (
            candidateRows.length === 0 ? renderList([], false) : (
              <section className="p-3 sm:p-4">
                <div className="flex flex-wrap justify-between items-center gap-2 mb-3">
                  <h2 className="text-lg font-medium">Inquilinos que deben pasar a jurídica</h2>
                  <p className="text-sm">Análisis del día {formatLocalDate(toLocalDateKey(new Date()))}</p>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-500">
                        {['Inm.', 'Inquilino', 'Garantía', 'Vencimiento', 'Deuda', 'Contrato', 'Acciones'].map(label => (
                          <th key={label} scope="col" className={`p-2 font-medium whitespace-nowrap ${label === 'Deuda' || label === 'Acciones' ? 'text-right' : 'text-left'}`}>{label}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {candidateRows.map(row => (
                        <tr key={row.pago_id} className="hover:bg-gray-50">
                          <td className="p-2 whitespace-nowrap">{row.inmueble_id}</td>
                          <td className="p-2">{[row.nombre, row.apellido].filter(Boolean).join(' ')}</td>
                          <td className="p-2">{row.garantia || 'Sin garantía'}</td>
                          <td className="p-2 whitespace-nowrap">{formatLocalDate(row.fecha_limite)}</td>
                          <td className="p-2 text-right whitespace-nowrap tabular-nums">
                            {row.moneda === 'USD' ? 'U$S' : row.moneda === 'UYU' ? '$' : row.moneda} {Number(row.monto).toLocaleString('es-UY', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </td>
                          <td className="p-2 whitespace-nowrap">{row.numero_contrato}</td>
                          <td className="p-2 text-right whitespace-nowrap">
                            <button type="button" onClick={() => addToLegalList(row.contrato_id)} aria-label={`Agregar contrato ${row.numero_contrato} a jurídica`} className="text-blue-600 hover:text-blue-900 font-medium">Agregar</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            )
          )}
        </div>

        {/* Criteria Modal */}
        {showCriteriaModal && editingCriteria && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
              <div className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Configurar Criterios</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Deuda Mínima (UYU)
                    </label>
                    <input
                      type="number"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2"
                      value={editingCriteria.monto_minimo}
                      onChange={(e) =>
                        setEditingCriteria({ ...editingCriteria, monto_minimo: Number(e.target.value) })
                      }
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Meses de Atraso Mínimos
                    </label>
                    <input
                      type="number"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2"
                      value={editingCriteria.meses_atraso}
                      onChange={(e) =>
                        setEditingCriteria({ ...editingCriteria, meses_atraso: Number(e.target.value) })
                      }
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Pagos Pendientes Mínimos
                    </label>
                    <input
                      type="number"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2"
                      value={editingCriteria.pagos_pendientes}
                      onChange={(e) =>
                        setEditingCriteria({ ...editingCriteria, pagos_pendientes: Number(e.target.value) })
                      }
                    />
                  </div>
                </div>
                <div className="flex gap-2 mt-6">
                  <button
                    onClick={saveCriteria}
                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Guardar
                  </button>
                  <button
                    onClick={() => setShowCriteriaModal(false)}
                    className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
}
